/**
 * useEffectiveVisualizerMode — Derives the effective visualizer mode for a panel.
 *
 * Implements a 3-level priority cascade using **only existing store data**:
 *
 *   Priority 1 (Highest): Pinned — Explicit visualizer step in pipeline
 *     → A visualizer tool is adjacent to the relevant step
 *     → Detected by useVisualizerStepDetection (bidirectional attachment)
 *
 *   Priority 2 (Middle):  Custom — User-selected mode override
 *     → User manually picked a mode from the panel dropdown
 *     → Tracked by useVisualizerStore per panel
 *
 *   Priority 3 (Lowest):  Auto — Default from semantic type
 *     → JSON→tree, CSV→table, Markdown→markdown, etc.
 *     → Derived from getDefaultModeForType()
 *
 * This hook is pure UI derivation. Zero store mutations. Zero engine changes.
 * All inputs come from existing stores/hooks that are already maintained
 * by the pipeline engine and user interactions.
 *
 * @see docs/design/editor-viz-cascade-store-analysis.md
 * @see docs/design/editor-viz-cascade-integration-plan.md
 */

import { useMemo } from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import { useVisualizerStepDetection, type VisualizerDetectionResult } from '@/hooks/useVisualizerStepDetection';
import type { IPipelineStep } from '@/interfaces/IPipeline';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import { isVisualizer } from '@/interfaces/ITool';
import { getDefaultModeForType } from '@/lib/visualizerManifest';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { type PanelRole, useVisualizerStore, type VisualizerMode } from '@/store/useVisualizerStore';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Which cascade level resolved the effective mode. */
export type CascadePriority = 'pinned' | 'custom' | 'auto';

/** Full cascade resolution result for a single panel. */
export interface CascadeResolution {
  /** The mode that should actually render on this panel. */
  effectiveMode: VisualizerMode;
  /** Which cascade level determined the effective mode. */
  priority: CascadePriority;
  /** Mode from explicit visualizer step (null if none attached). */
  pinnedMode: VisualizerMode | null;
  /** Mode the user explicitly selected (null if matches auto-default). */
  customMode: VisualizerMode | null;
  /** Mode auto-derived from semantic type (always present, falls back to 'raw'). */
  autoMode: VisualizerMode;
  /** Step ID of the pinned visualizer (null if none). */
  pinnedStepId: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure resolution logic (testable without React)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Find the first non-visualizer step ID in the pipeline.
 * Used to determine the relevant step for the input panel in global context.
 */
function findFirstRealStepId(pipeline: IPipelineStep[]): string | null {
  for (const step of pipeline) {
    const tool = ToolRegistry.get(step.toolId);
    if (tool && !isVisualizer(tool)) return step.id;
  }
  return null;
}

/**
 * Find the last non-visualizer step ID in the pipeline.
 * Used to determine the relevant step for the output panel in global context.
 */
function findLastRealStepId(pipeline: IPipelineStep[]): string | null {
  for (let i = pipeline.length - 1; i >= 0; i--) {
    const tool = ToolRegistry.get(pipeline[i].toolId);
    if (tool && !isVisualizer(tool)) return pipeline[i].id;
  }
  return null;
}

/**
 * Resolve cascade priority from the three input levels.
 * Pure function — no store access, no side effects.
 */
export function resolveCascade(
  pinnedMode: VisualizerMode | null,
  pinnedStepId: string | null,
  storeMode: VisualizerMode,
  autoMode: VisualizerMode,
): CascadeResolution {
  // Priority 1: Pinned (explicit visualizer step overrides everything)
  if (pinnedMode) {
    return {
      effectiveMode: pinnedMode,
      priority: 'pinned',
      pinnedMode,
      customMode: storeMode !== autoMode ? storeMode : null,
      autoMode,
      pinnedStepId,
    };
  }

  // Priority 2: Custom (user selected a different mode than auto-default)
  if (storeMode !== autoMode) {
    return {
      effectiveMode: storeMode,
      priority: 'custom',
      pinnedMode: null,
      customMode: storeMode,
      autoMode,
      pinnedStepId: null,
    };
  }

  // Priority 3: Auto (semantic type default)
  return {
    effectiveMode: autoMode,
    priority: 'auto',
    pinnedMode: null,
    customMode: null,
    autoMode,
    pinnedStepId: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// React Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives the effective visualizer mode for a panel using the 3-level
 * priority cascade. Reads from existing stores only — no mutations.
 *
 * @param panel - Which IO panel ('input' or 'output')
 * @param stepId - Optional step ID for per-step panel context.
 *   When omitted, auto-detects the relevant step:
 *   input panel → first real step, output panel → last real step.
 */
export function useEffectiveVisualizerMode(
  panel: PanelRole,
  stepId?: string,
): CascadeResolution {
  // ── Read user's selected mode (Priority 2: Custom) ──
  const storeMode = useVisualizerStore(
    (s) => (panel === 'input' ? s.inputPanel : s.outputPanel).mode,
  );

  // ── Read pipeline and detect pinned visualizer steps (Priority 1: Pinned) ──
  const pipeline = useEditorStore((s) => s.pipeline);
  const detection: VisualizerDetectionResult = useVisualizerStepDetection(pipeline);

  // ── Read semantic type for auto-default (Priority 3: Auto) ──
  const inputSemanticType = useEditorStore((s) => s.inputType);
  const outputSemanticType = useExecutionStore((s) => {
    const results = s.stepResults;
    if (results.length === 0) return null;
    return results[results.length - 1]?.outputType ?? null;
  });
  const semanticType: ISemanticType | null | undefined = panel === 'input'
    ? inputSemanticType
    : outputSemanticType;

  // ── Memoized cascade resolution ──
  return useMemo(() => {
    // Determine which step to check for pinned overrides
    const effectiveStepId =
      stepId ??
      (panel === 'input'
        ? findFirstRealStepId(pipeline)
        : findLastRealStepId(pipeline));

    // Look up pinned override from detection results
    const overrideMap =
      panel === 'input' ? detection.inputOverrides : detection.outputOverrides;
    const override = effectiveStepId ? overrideMap.get(effectiveStepId) : undefined;

    const pinnedMode = override?.mode ?? null;
    const pinnedStepId = override?.visualizerStepId ?? null;

    // Compute auto-default from semantic type
    const autoMode = getDefaultModeForType(semanticType);

    return resolveCascade(pinnedMode, pinnedStepId, storeMode, autoMode);
  }, [panel, stepId, pipeline, detection, storeMode, semanticType]);
}
